import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  Legend,
} from 'recharts';

/**
 * This placeholder represents a stacked bar chart that displays daily unique users
 * split by account type (external vs internal).
 *
 * Expected data shape:
 * [
 *   { date: "Jun 01", external: 12, internal: 3 },
 *   { date: "Jun 02", external: 15, internal: 5 },
 *   ...
 * ]
 *
 * Recharts example (uncomment when library is installed):
 * <ResponsiveContainer width="100%" height={256}>
 *   <BarChart data={data} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
 *     <CartesianGrid strokeDasharray="3 3" />
 *     <XAxis dataKey="date" />
 *     <YAxis allowDecimals={false} />
 *     <Tooltip />
 *     <Bar dataKey="external" stackId="users" fill={colors[0]} />
 *     <Bar dataKey="internal" stackId="users" fill={colors[1]} />
 *   </BarChart>
 * </ResponsiveContainer>
 */

const formatDateForDisplay = (isoDate) => {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-');
  return new Date(Number(y), Number(m) - 1, Number(d))
    .toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
    });
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 border border-gray-200 shadow-sm rounded-md">
        <p className="text-sm font-semibold">{formatDateForDisplay(label)}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const StackedBarChartPlaceholder = ({
  title = 'Daily New Unique Users',
  subtitle = '',
  data = [],
  categories = ['External (English)', 'External (Spanish)', 'Internal'],
  colors = ['#10B981', '#F59E0B', '#3B82F6'], // green, yellow, blue
  accountTypeFilter = null,
}) => {

  // Default parameters for axes
  const xAxisProps = {
    dataKey: "date",
    tick: { fontSize: 12 },
    height: 60,
    scale: "auto",
    padding: { left: 0, right: 0 }
  };

  const yAxisProps = {
    allowDecimals: false,
    tick: { fontSize: 12 },
    width: 60,
    scale: "auto",
    padding: { top: 20, bottom: 0 }
  };

  return (
    <Card className="p-6">
      <CardHeader className="p-0 mb-4">
        <CardTitle className="text-lg font-semibold">{title}</CardTitle>
        {subtitle && <p className="text-sm text-gray-600 mt-1">{subtitle}</p>}
      </CardHeader>
      <CardContent className="p-0">
        {/* Legend */}
        <div className="flex items-center space-x-6 mb-4">
          {accountTypeFilter ? (
            <div className="flex items-center space-x-2">
              <div
                className="w-4 h-4 rounded-sm"
                style={{ 
                  backgroundColor: 
                    accountTypeFilter === 'External (English)' ? '#10B981' :
                    accountTypeFilter === 'External (Spanish)' ? '#F59E0B' :
                    accountTypeFilter === 'Internal' ? '#3B82F6' : colors[0]
                }}
              ></div>
              <span className="text-sm text-gray-600">{accountTypeFilter}</span>
            </div>
          ) : (
            categories.map((category, index) => (
              <div key={category} className="flex items-center space-x-2">
                <div
                  className="w-4 h-4 rounded-sm"
                  style={{ backgroundColor: colors[index] }}
                ></div>
                <span className="text-sm text-gray-600">{category}</span>
              </div>
            ))
          )}
        </div>

        {/* Chart */}
        {data && data.length > 0 ? (
          <ResponsiveContainer width="100%" height={256}>
            <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis {...xAxisProps} />
              <YAxis {...yAxisProps} />
              <Tooltip content={<CustomTooltip />} />
              <Legend verticalAlign="top" height={36} />
              {accountTypeFilter ? (
                <Bar 
                  dataKey={accountTypeFilter} 
                  stackId="users" 
                  fill={
                    accountTypeFilter === 'External (English)' ? '#10B981' :
                    accountTypeFilter === 'External (Spanish)' ? '#F59E0B' :
                    accountTypeFilter === 'Internal' ? '#3B82F6' : colors[0]
                  } 
                  name={accountTypeFilter} 
                />
              ) : (
                <>
                  <Bar 
                    dataKey="External (English)" 
                    stackId="users" 
                    fill="#10B981" 
                    name="External (English)" 
                  />
                  <Bar 
                    dataKey="External (Spanish)" 
                    stackId="users" 
                    fill="#F59E0B" 
                    name="External (Spanish)" 
                  />
                  <Bar 
                    dataKey="Internal" 
                    stackId="users" 
                    fill="#3B82F6" 
                    name="Internal" 
                  />
                </>
              )}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-200">
            <div className="text-center">
              <div className="text-gray-400 mb-2">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" 
                  />
                </svg>
              </div>
              <p className="text-gray-500 text-sm">Bar chart will be displayed here</p>
              {data.length === 0 && (
                <p className="text-gray-400 text-xs mt-1">Ready for backend data integration</p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StackedBarChartPlaceholder; 